import React, { useState, useEffect, useContext } from 'react';
import {
    Row,
    Col,
    Input,
    Label,
    Button,
    Table,
    Card,
    CardBody,
    Modal,
    ModalHeader,
    ModalBody,
} from 'reactstrap';
import { PocketBaseContext } from '../../../PocketBaseContext';

const GetCustomerInfo = ({ onCustomerSelect, initialCustomerInfo = null, initialSelectedContacts = [] }) => {
    const pb = useContext(PocketBaseContext);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(initialCustomerInfo);
    const [selectedContacts, setSelectedContacts] = useState(initialSelectedContacts);
    const [searchTerm, setSearchTerm] = useState('');
    const [contactSearchTerm, setContactSearchTerm] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contactsModalOpen, setContactsModalOpen] = useState(false);
    const [personnelList, setPersonnelList] = useState([]);

    // Fetch customers and their related personnel
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                const records = await pb.collection('customers').getList(1, 50, {
                    sort: 'name',
                    expand: 'customer_contact'
                });
                setCustomers(records.items);
                setFilteredCustomers(records.items);
            } catch (error) {
                console.error('Error fetching customers:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (pb) {
            fetchCustomers();
        }
    }, [pb]);

    useEffect(() => {
        const initializeInitialData = async () => {
            if (initialCustomerInfo) {
                setSelectedCustomer(initialCustomerInfo);
                const personnel = await fetchPersonnel(initialCustomerInfo);
                setPersonnelList(personnel);
            }
            if (initialSelectedContacts) {
                setSelectedContacts(initialSelectedContacts);
            }
        };
        initializeInitialData();
    }, [initialCustomerInfo, initialSelectedContacts]);

    // Fetch personnel for selected customer
    const fetchPersonnel = async (customer) => {
        try {
            // First, get the detailed customer record with customer_contact relations
            const customerRecord = await pb.collection('customers').getOne(customer.id, {
                expand: 'customer_contact'
            });

            if (!customerRecord.customer_contact || customerRecord.customer_contact.length === 0) {
                console.log('No personnel found for customer');
                return [];
            }

            console.log('Customer contact IDs:', customerRecord.customer_contact);

            // Now fetch the personnel records using these IDs
            const filter = customerRecord.customer_contact.map(id => `id = "${id}"`).join(' || ');
            const records = await pb.collection('personel').getList(1, 50, {
                filter: filter
            });

            console.log('Fetched personnel records:', records.items);
            return records.items;

        } catch (error) {
            console.error('Error fetching personnel:', error);
            return [];
        }
    };


    // GetCustomerInfo.js içinde handleCustomerSelect fonksiyonunu güncelleyelim
    const handleCustomerSelect = async (customer) => {
        try {
            console.log('Selected customer:', customer);
            const personnel = await fetchPersonnel(customer);
            console.log('Fetched personnel:', personnel);

            // Customer nesnesini oluştururken selectedContacts'ı da ekleyelim
            const updatedCustomer = {
                ...customer,
                showAdditionalNotes: customer.showAdditionalNotes || false,
                additionalNotes: customer.additionalNotes || '',
                personnel: personnel,
                selectedContacts: [] // Başlangıçta boş dizi olarak ekleyelim
            };

            setSelectedCustomer(updatedCustomer);
            setPersonnelList(personnel);
            setSelectedContacts([]); // Yeni müşteri seçildiğinde seçili kişileri sıfırla

            // Parent componente güncellenmiş müşteri bilgisini gönder
            onCustomerSelect(updatedCustomer);

            setContactsModalOpen(true);
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error selecting customer:', error);
            setError('Müşteri personel bilgileri yüklenirken hata oluştu');
        }
    };

    // handleContactSelect fonksiyonunu da güncelleyelim
    const handleContactSelect = (contact, isSelected) => {
        let newContacts;
        if (isSelected) {
            newContacts = [...selectedContacts, contact];
        } else {
            newContacts = selectedContacts.filter(c => c.id !== contact.id);
        }
        setSelectedContacts(newContacts);

        // Müşteri bilgisini güncellerken selectedContacts'ı da güncelle
        const updatedCustomer = {
            ...selectedCustomer,
            selectedContacts: newContacts // Seçili kişileri güncelle
        };
        setSelectedCustomer(updatedCustomer);
        onCustomerSelect(updatedCustomer);
    };

    // Render contact details in modal
    const renderContactItem = (contact) => (
        <div key={contact.id} className="contact-item border rounded p-3 mb-2">
            <div className="d-flex align-items-center">
                <Input
                    type="checkbox"
                    className="me-3"
                    checked={selectedContacts.some(c => c.id === contact.id)}
                    onChange={(e) => handleContactSelect(contact, e.target.checked)}
                />
                <div className="flex-grow-1">
                    <h6 className="mb-1">{contact.name} {contact.surname}</h6>
                    <div className="text-muted small mb-1">
                        {contact.position && <span className="me-2">{contact.position}</span>}
                        {contact.department && <span>• {contact.department}</span>}
                    </div>
                    <div className="contact-info small">
                        {contact.email && (
                            <span className="me-2">
                                <i className="ri-mail-line me-1"></i>
                                {contact.email}
                            </span>
                        )}
                        {contact.phone && (
                            <span className="me-2">
                                <i className="ri-phone-line me-1"></i>
                                {contact.phone}
                            </span>
                        )}
                        {contact.cellphone && (
                            <span>
                                <i className="ri-smartphone-line me-1"></i>
                                {contact.cellphone}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // Filter contacts based on search term
    const filteredContacts = personnelList.filter(contact =>
        !contactSearchTerm ||
        `${contact.name} ${contact.surname}`.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
        contact.phone?.includes(contactSearchTerm) ||
        contact.cellphone?.includes(contactSearchTerm)
    );

    return (
        <div>
            {/* Customer selection header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="text-muted text-uppercase fw-semibold mb-0">
                    Müşteri Bilgileri
                </h5>
                <Button color="primary" size="sm" onClick={() => setIsModalOpen(true)}>
                    <i className="ri-user-search-line align-middle me-1"></i>
                    Müşteri Seç
                </Button>
            </div>

            {/* Selected customer display */}
            {selectedCustomer ? (
                <Card className="border">
                    <CardBody>
                        <Row>
                            {/* Customer details */}
                            <Col md={9}>
                                <Row>
                                    {/* Company Info */}
                                    <Col md={4} className="border-end">
                                        <div className="mb-4">
                                            <h5 className="fw-semibold mb-3">{selectedCustomer.name}</h5>
                                            <div className="mb-3">
                                                <Label className="text-muted fs-12 mb-1">Şirket Adresi</Label>
                                                <p className="mb-0 fw-medium">{selectedCustomer.customer_address || '-'}</p>
                                            </div>
                                            <div className="mb-3">
                                                <Label className="text-muted fs-12 mb-1">Vergi No</Label>
                                                <p className="mb-0 fw-medium">{selectedCustomer.tax_number || '-'}</p>
                                            </div>
                                            <div>
                                                <Label className="text-muted fs-12 mb-1">Vergi Dairesi</Label>
                                                <p className="mb-0 fw-medium">{selectedCustomer.tax_office || '-'}</p>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Customer Type */}
                                    <Col md={4} className="border-end">
                                        <div className="mb-3">
                                            <Label className="text-muted fs-12 mb-1">Müşteri Tipi</Label>
                                            <p className="mb-0 fw-medium">{selectedCustomer.kurumsallik || '-'}</p>
                                        </div>
                                    </Col>

                                    {/* Additional Notes Section */}
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center mb-2">
                                                <Input
                                                    type="checkbox"
                                                    id="showAdditionalNotes"
                                                    className="me-2"
                                                    checked={selectedCustomer.showAdditionalNotes || false}
                                                    onChange={(e) => {
                                                        const updatedCustomer = {
                                                            ...selectedCustomer,
                                                            showAdditionalNotes: e.target.checked,
                                                            additionalNotes: e.target.checked ? (selectedCustomer.additionalNotes || '') : ''
                                                        };
                                                        setSelectedCustomer(updatedCustomer);
                                                        onCustomerSelect(updatedCustomer);
                                                    }}
                                                />
                                                <Label className="text-muted fs-12 mb-0" for="showAdditionalNotes">
                                                    Özel Not Ekle (PDF'te görünecek)
                                                </Label>
                                            </div>

                                            {selectedCustomer.showAdditionalNotes && (
                                                <div className="mt-2">
                                                    <Input
                                                        type="textarea"
                                                        className="form-control"
                                                        placeholder="Müşteri veya teklif ile ilgili özel notlar..."
                                                        rows="4"
                                                        value={selectedCustomer.additionalNotes || ''}
                                                        onChange={(e) => {
                                                            const updatedCustomer = {
                                                                ...selectedCustomer,
                                                                additionalNotes: e.target.value
                                                            };
                                                            setSelectedCustomer(updatedCustomer);
                                                            onCustomerSelect(updatedCustomer);
                                                        }}
                                                    />
                                                    <small className="text-muted mt-1 d-block">
                                                        Bu not PDF raporunda müşteri bilgileri altında görünecektir.
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Selected contacts display */}
                            <Col md={3} className="border-start">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Label className="text-muted fs-12 mb-0">İlgili Kişiler</Label>
                                    <Button
                                        color="soft-primary"
                                        size="sm"
                                        onClick={() => setContactsModalOpen(true)}
                                    >
                                        <i className="ri-user-add-line"></i>
                                    </Button>
                                </div>

                                <div className="related-contacts">
                                    {selectedContacts.map((contact) => (
                                        <div key={contact.id} className="contact-item border-bottom py-2">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <h6 className="mb-1 fs-13">{contact.name} {contact.surname}</h6>
                                                    {contact.position && (
                                                        <div className="text-muted small mb-1">{contact.position}</div>
                                                    )}
                                                    {contact.department && (
                                                        <div className="text-muted small mb-1">
                                                            <i className="ri-building-line me-1"></i>
                                                            {contact.department}
                                                        </div>
                                                    )}
                                                    {contact.email && (
                                                        <a href={`mailto:${contact.email}`} className="text-reset d-block small">
                                                            <i className="ri-mail-line me-1"></i>
                                                            {contact.email}
                                                        </a>
                                                    )}
                                                    {contact.phone && (
                                                        <a href={`tel:${contact.phone}`} className="text-reset d-block small">
                                                            <i className="ri-phone-line me-1"></i>
                                                            {contact.phone}
                                                        </a>
                                                    )}
                                                    {contact.cellphone && (
                                                        <a href={`tel:${contact.cellphone}`} className="text-reset d-block small">
                                                            <i className="ri-smartphone-line me-1"></i>
                                                            {contact.cellphone}
                                                        </a>
                                                    )}
                                                </div>
                                                <Button
                                                    color="soft-danger"
                                                    size="sm"
                                                    className="btn-icon"
                                                    onClick={() => handleContactSelect(contact, false)}
                                                >
                                                    <i className="ri-close-line"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {selectedContacts.length === 0 && (
                                        <div className="text-center text-muted small py-3">
                                            Henüz kişi seçilmedi
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            ) : (
                <div className="bg-light p-3 rounded text-center">
                    <p className="mb-0 text-muted">Henüz müşteri seçimi yapılmadı</p>
                </div>
            )}

            {/* Customer selection modal */}
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="xl">
                <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>Müşteri Seçimi</ModalHeader>
                <ModalBody>
                    <Row className="mb-3">
                        <Col>
                            <Input
                                type="text"
                                placeholder="Müşteri adı, vergi no veya vergi dairesi ile ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Yükleniyor...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>Firma Adı</th>
                                        <th>Vergi No</th>
                                        <th>Vergi Dairesi</th>
                                        <th>Tip</th>
                                        <th>İlgili Kişi Sayısı</th>
                                        <th>İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCustomers.map((customer) => (
                                        <tr key={customer.id}>
                                            <td>{customer.name}</td>
                                            <td>{customer.tax_number || '-'}</td>
                                            <td>{customer.tax_office || '-'}</td>
                                            <td>{customer.kurumsallik || '-'}</td>
                                            <td>
                                                {customer.expand?.customer_contact?.length || 0} kişi
                                            </td>
                                            <td>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => handleCustomerSelect(customer)}
                                                >
                                                    Seç
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredCustomers.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center">
                                                {searchTerm ? 'Arama kriterlerine uygun müşteri bulunamadı' : 'Müşteri listesi boş'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {error}
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* Contact selection modal */}
            <Modal isOpen={contactsModalOpen} toggle={() => setContactsModalOpen(!contactsModalOpen)} size="lg">
                <ModalHeader toggle={() => setContactsModalOpen(!contactsModalOpen)}>
                    İlgili Kişi Seçimi - {selectedCustomer?.name}
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <Input
                            type="text"
                            placeholder="İsim, email veya telefon ile ara..."
                            value={contactSearchTerm}
                            onChange={(e) => setContactSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="contact-list">
                        {filteredContacts.map(contact => renderContactItem(contact))}
                    </div>
                    <div className="text-end mt-3">
                        <Button color="light" className="me-2" onClick={() => setContactsModalOpen(false)}>
                            İptal
                        </Button>
                        <Button color="primary" onClick={() => setContactsModalOpen(false)}>
                            Seçimi Tamamla ({selectedContacts.length} kişi)
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default GetCustomerInfo;